import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  type OnChanges,
  type OnDestroy,
  Output,
} from '@angular/core';
import { FormBuilder, type FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type {
  EditWorkPosition,
  WorkPosition,
} from '../../../shared/models/api/work-position.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { WorkPositionService } from '../../../shared/services/api/work-position.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { EditWorkPositionFormValue } from './work-position-edit.models';

@Component({
  selector: 'opa-work-position-edit',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './work-position-edit.component.html',
  styleUrl: './work-position-edit.component.scss',
})
export class WorkPositionEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly workPositionService = inject(WorkPositionService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) workPosition: WorkPosition | undefined = undefined;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<WorkPositionEditComponent>): void {
    if (changes.open && this.open) this.resetForm(this.workPosition);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid || !this.workPosition) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const workPosition = this.formValueToWorkPosition(formValue);
    this.subSink.sink = this.workPositionService
      .editWorkPosition(this.workPosition.id, workPosition)
      .pipe(
        tap({
          next: (editedWorkPosition) => {
            this.toastManager.addMessage({
              text: `Work Position '${editedWorkPosition.description}' edited`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditWorkPositionFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditWorkPositionFormValue>>({
      description: this.formBuilder.control(null, [OpaValidators.required()]),
    });

    return form;
  }

  private formValueToWorkPosition(form: EditWorkPositionFormValue): EditWorkPosition {
    const workPosition: EditWorkPosition = {
      description: form.description!,
      name: this.workPosition!.name,
    };
    return workPosition;
  }

  private resetForm(workPosition?: WorkPosition): void {
    this.form.reset();
    if (!workPosition) return;

    this.form.patchValue({
      description: workPosition.description,
    });
  }
}
